import { useMemo } from 'react';
import { Dictionary, IHomepageMainNavItems } from "../../../types/working-model";

interface RouteMapProps {
	eventMainNavItems?: IHomepageMainNavItems[];
	debug?: boolean;
}

export const DebugDefaultRouteMap = {
	CustomPage: "/event/:uuid/:language/pages/:customPath",
	Session: "/event/:uuid/:language/session/:sessionUuid",
	Fireside: "/event/:uuid/:language/session/:sessionUuid/fireside",
	FiresideNoHomepage: "/event/:uuid/:language/fireside",
	Breakout: "/event/:uuid/:language/session/:sessionUuid/breakout/:breakoutUuid",
	Registration: "/event/:uuid/:language/registration",
	PrivacyPolicy: "/event/:uuid/:language/privacy-policy",
	TermsConditions: "/event/:uuid/:language/terms-conditions",
	Home: `/event/:uuid/:language/home`,
	Sessions: `/event/:uuid/:language/sessions`,
	People: `/event/:uuid/:language/people`,
	Directory: '/event/:uuid/:language/directory',
	Profile: "/event/:uuid/:language/profile",
	AttendeeProfile: "/event/:uuid/:language/profile/:attendeeId",
	Landing: `/event/:uuid/:language`,
	LandingRedirect: "/event/:uuid",
	Leaderboard: `/event/:uuid/:language/leaderboard`,
};

export const DefaultRouteMap = {
	CustomPage: "/:eventName/:language/pages/:customPath",
	Session: "/:eventName/:language/session/:sessionUuid",
	Breakout: "/:eventName/:language/session/:sessionUuid/breakout/:breakoutUuid",
	Fireside: "/:eventName/:language/session/:sessionUuid/fireside",
	FiresideNoHomepage: "/:eventName/:language/fireside",
	Registration: "/:eventName/:language/registration/:sessionUuid?",
	PrivacyPolicy: "/:eventName/:language/privacy-policy",
	TermsConditions: "/:eventName/:language/terms-conditions",
	Home: `/:eventName/:language/home`,
	Sessions: `/:eventName/:language/sessions`,
	People: `/:eventName/:language/people`,
	Directory: `/:eventName/:language/directory`,
	Profile: "/:eventName/:language/profile",
	AttendeeProfile: "/:eventName/:language/profile/:attendeeId",
	Landing: `/:eventName/:language`,
	LandingRedirect: "/:eventName",
	Leaderboard: `/:eventName/:language/leaderboard`,
};

export default function useRouteMap({ eventMainNavItems, debug }: RouteMapProps): Dictionary {
	return useMemo(() => {
		let routeMap: Dictionary = {};

		// TODO: Post MVP when we do custom tab names decide if we want to use custom_name or
		// name for the endpoint. if custom_name, update marketing-nav to use that as well.
		const customRouteMap = eventMainNavItems?.reduce((a: { [key: string]: string }, c: IHomepageMainNavItems) => {
			a[c.name] = `${debug ? '/event/:uuid' : '/:eventName'}/:language/${c.name.toLowerCase()}`;
			return a;
		}, {});

		let defaultRouteMap: Dictionary = {};

		if (!debug) {
			defaultRouteMap = DefaultRouteMap;
		} else {
			defaultRouteMap = DebugDefaultRouteMap;
		}

		if (customRouteMap) {
			if (!debug) {
				customRouteMap.Profile = "/:eventName/:language/profile";
				customRouteMap.AttendeeProfile = "/:eventName/:language/profile/:attendeeId";
				customRouteMap.Directory = "/:eventName/:language/directory";
			} else {
				customRouteMap.Profile = "/event/:uuid/:language/profile";
				customRouteMap.AttendeeProfile = "/event/:uuid/:language/profile/:attendeeId";
				customRouteMap.Directory = "/event/:uuid/:language/directory";
			}

			routeMap = customRouteMap;
		} else {
			routeMap = defaultRouteMap;
		}

		return routeMap;
	}, [debug, eventMainNavItems]);
}

export const getUsedPathnames = (): Set<string> => {
	const routeMap = useRouteMap({ debug: false }); //we want to check the live event urls route map
	return useMemo(() => {

		const usedPathNames = new Set<string>([]);
		Object.values(routeMap).forEach((route: string) => {
			route.split("/")//get individual path names
				.forEach(path => {
					if (path && path[0] !== ":") { //if the path isnt a parameter variable (e.g. starts with ":"")
						usedPathNames.add(path);
					}
				});
		});
		return usedPathNames;
	}, [routeMap]);
};